import { getAuthToken, setAuthToken } from '../lib/authToken';

export function getApiBase(): string {
  return import.meta.env.VITE_API_URL ?? '/api';
}

/** @deprecated Prefer getAuthToken() or useAuthToken() for reactive reads. */
export function getToken(): string | null {
  return getAuthToken();
}

export function setToken(token: string | null): void {
  setAuthToken(token);
}

export class ApiError extends Error {
  readonly status: number;
  readonly isNetworkError: boolean;

  constructor(message: string, status: number, isNetworkError = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.isNetworkError = isNetworkError;
  }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  };
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${getApiBase()}${path}`, { ...options, headers });
  } catch {
    throw new ApiError(
      'Cannot reach the API server. Start it with npm run dev:server (or npm run dev:all).',
      0,
      true,
    );
  }

  if (!res.ok) {
    const body = await res.text();
    let message = body || res.statusText;
    try {
      const json = JSON.parse(body) as { error?: { message?: string }; message?: string };
      message = json.error?.message ?? json.message ?? message;
    } catch {
      // body is plain text
    }
    if (res.status === 401 && token) {
      setToken(null);
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PATCH',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export async function apiDelete<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'DELETE' });
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PUT',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}
